import React from 'react';
import { IconButton, IconButtonProps, styled } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import {
  GRADIENT_PRIMARY,
  pulseAnimation,
  SECONDARY_TEXT_COLOR,
  SECONDARY_TEXT_COLOR_HOVER,
} from 'components/AI/AIStyles';

// Define props interface for the styled component
interface StyledAIIconButtonProps {
  variant?: 'contained' | 'text';
  theme?: any;
}

// Create a styled IconButton with a smooth gradient direction change effect
const StyledAIIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'variant',
})((props: StyledAIIconButtonProps) => {
  const { theme, variant } = props;
  return {
    position: 'relative',
    transition: 'all ease-in-out 350ms',
    color: SECONDARY_TEXT_COLOR,
    '&:hover': {
      color: SECONDARY_TEXT_COLOR_HOVER,
      animation: `${pulseAnimation} 2s 1`,
      backgroundColor: 'transparent',
      boxShadow: 'none',
    },
    ...(variant === 'contained' && {
      border: '0',
      background: GRADIENT_PRIMARY,
      color: theme.palette.common.white,
      '&:hover': {
        color: theme.palette.common.white,
        animation: `${pulseAnimation} 2s 1`,
        boxShadow: '0px 5px 20px rgba(0, 0, 0, 0.1)',
      },
    }),
  };
});

export interface AIIconButtonProps extends Omit<IconButtonProps, 'children'> {
  /**
   * Custom icon to display instead of the default AI icon
   */
  customIcon?: React.ReactNode;
  /**
   * Variant of the button
   */
  variant?: 'contained' | 'text';
}

/**
 * AI Icon Button component with a sparkling icon
 *
 * @param props - Component props
 * @returns React component
 */
export const AIIconButton: React.FC<AIIconButtonProps> = ({
  customIcon,
  variant = 'text',
  ...rest
}) => {
  // Extract variant from props to avoid passing it to the underlying IconButton
  const styledProps = { variant } as StyledAIIconButtonProps;

  return (
    <StyledAIIconButton disableRipple {...styledProps} {...rest}>
      {customIcon || <AutoAwesome />}
    </StyledAIIconButton>
  );
};

export default AIIconButton;
